import React, { createContext, useContext, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import fr from "@/locales/fr.json";
import en from "@/locales/en.json";

const dictionaries = { fr, en };
export const SUPPORTED_LOCALES = ["fr", "en"];
export const DEFAULT_LOCALE = "fr";

const I18nContext = createContext({ locale: DEFAULT_LOCALE, t: (k) => k, dict: fr });

function resolvePath(obj, path) {
  if (!obj) return undefined;
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

export function I18nProvider({ children }) {
  const params = useParams();
  const location = useLocation();

  const locale = useMemo(() => {
    if (params && params.lang && SUPPORTED_LOCALES.includes(params.lang)) {
      return params.lang;
    }
    const seg = location.pathname.split("/").filter(Boolean)[0];
    if (SUPPORTED_LOCALES.includes(seg)) return seg;
    return DEFAULT_LOCALE;
  }, [params, location.pathname]);

  const dict = dictionaries[locale] || dictionaries[DEFAULT_LOCALE];

  const value = useMemo(() => {
    const t = (key) => {
      const val = resolvePath(dict, key);
      return val === undefined ? key : val;
    };
    return { locale, t, dict };
  }, [dict, locale]);

  // Set the document lang attribute for accessibility & SEO
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
